import {
  confirmCheckoutSession,
  getCheckoutReview,
  saveBillingAddress,
  saveShippingAddress,
  saveShippingMethod,
} from '../../api'
import { checkoutAddressToForm, checkoutFormToAddress, resolveNextStepFromSession } from '../CheckoutFlow.types'
import {
  clearActiveCheckoutSessionId,
  pushCheckoutStepInUrl,
  replaceCheckoutStepInUrl,
} from '../checkoutUrlState'
import { resolveSubmitError } from './errors'
import { applySessionUpdate } from './session'
import { validateAddressForm } from './validation'
import type { TranslateFn } from './types'
import type { CheckoutFlowAction, CheckoutFlowState } from '../CheckoutFlow.types'

export const submitShippingAddress = async ({
  state,
  dispatch,
  token,
  t,
}: {
  state: CheckoutFlowState
  dispatch: (action: CheckoutFlowAction) => void
  token: string | null
  t: TranslateFn
}) => {
  const activeSession = state.session
  if (!activeSession) return

  const validationErrors = validateAddressForm({ form: state.shippingAddressForm, t })
  if (validationErrors.length > 0) {
    dispatch({ type: 'SET_VALIDATION_ERRORS', validationErrors })
    return
  }

  dispatch({ type: 'SUBMIT_STARTED' })
  dispatch({ type: 'CLEAR_VALIDATION_ERRORS' })

  try {
    const session = await saveShippingAddress({
      token,
      id: activeSession.id,
      payload: {
        address: checkoutFormToAddress(state.shippingAddressForm),
        sameAsShipping: state.sameAsShipping,
      },
    })

    applySessionUpdate({ dispatch, state, session })
  } catch (error) {
    const mapped = resolveSubmitError({ error, fallbackCode: 'backend_validation_error' })
    dispatch({ type: 'SET_ERROR', code: mapped.code, detail: mapped.detail })
  } finally {
    dispatch({ type: 'SUBMIT_COMPLETED' })
  }
}

export const submitBillingAddress = async ({
  state,
  dispatch,
  token,
  t,
}: {
  state: CheckoutFlowState
  dispatch: (action: CheckoutFlowAction) => void
  token: string | null
  t: TranslateFn
}) => {
  const activeSession = state.session
  if (!activeSession) return

  if (!state.sameAsShipping) {
    const validationErrors = validateAddressForm({ form: state.billingAddressForm, t })
    if (validationErrors.length > 0) {
      dispatch({ type: 'SET_VALIDATION_ERRORS', validationErrors })
      return
    }
  }

  dispatch({ type: 'SUBMIT_STARTED' })
  dispatch({ type: 'CLEAR_VALIDATION_ERRORS' })

  try {
    const session = await saveBillingAddress({
      token,
      id: activeSession.id,
      payload: {
        sameAsShipping: state.sameAsShipping,
        address: state.sameAsShipping
          ? checkoutFormToAddress(state.shippingAddressForm)
          : checkoutFormToAddress(state.billingAddressForm),
      },
    })

    applySessionUpdate({ dispatch, state, session })
  } catch (error) {
    const mapped = resolveSubmitError({ error, fallbackCode: 'backend_validation_error' })
    dispatch({ type: 'SET_ERROR', code: mapped.code, detail: mapped.detail })
  } finally {
    dispatch({ type: 'SUBMIT_COMPLETED' })
  }
}

export const submitShippingMethod = async ({
  state,
  dispatch,
  token,
}: {
  state: CheckoutFlowState
  dispatch: (action: CheckoutFlowAction) => void
  token: string | null
}) => {
  const activeSession = state.session
  if (!activeSession) return

  dispatch({ type: 'SUBMIT_STARTED' })
  dispatch({ type: 'CLEAR_VALIDATION_ERRORS' })

  try {
    const session = await saveShippingMethod({
      token,
      id: activeSession.id,
      payload: {
        shippingMethod: state.selectedShippingMethod,
      },
    })

    applySessionUpdate({ dispatch, state, session })

    const reviewPayload = await getCheckoutReview({ token, id: session.id })
    dispatch({
      type: 'SESSION_UPDATED',
      session: reviewPayload.session,
      shippingAddressForm: checkoutAddressToForm(reviewPayload.session.shippingAddress),
      billingAddressForm: checkoutAddressToForm(reviewPayload.session.billingAddress),
      selectedShippingMethod: reviewPayload.session.shippingMethod?.code ?? state.selectedShippingMethod,
      selectedPaymentProvider:
        reviewPayload.session.selectedPaymentProvider
        ?? reviewPayload.defaultPaymentProvider
        ?? state.selectedPaymentProvider,
      step: resolveNextStepFromSession(reviewPayload.session),
    })
    pushCheckoutStepInUrl(resolveNextStepFromSession(reviewPayload.session))
    dispatch({
      type: 'REVIEW_UPDATED',
      review: reviewPayload.review,
      shippingMethods: reviewPayload.shippingMethods,
      paymentProviders: reviewPayload.paymentProviders ?? [],
      defaultPaymentProvider: reviewPayload.defaultPaymentProvider ?? state.selectedPaymentProvider,
    })
  } catch (error) {
    const mapped = resolveSubmitError({ error, fallbackCode: 'backend_validation_error' })
    dispatch({ type: 'SET_ERROR', code: mapped.code, detail: mapped.detail })
  } finally {
    dispatch({ type: 'SUBMIT_COMPLETED' })
  }
}

export const submitCheckoutConfirmation = async ({
  state,
  dispatch,
  token,
  toLocalizedPath,
  onCheckoutCompleted,
}: {
  state: CheckoutFlowState
  dispatch: (action: CheckoutFlowAction) => void
  token: string | null
  toLocalizedPath: (path: string) => string
  onCheckoutCompleted: (consumedCartId: string) => Promise<void>
}) => {
  const activeSession = state.session
  if (!activeSession) return

  dispatch({ type: 'SUBMIT_STARTED' })

  try {
    const confirm = await confirmCheckoutSession({
      token,
      id: activeSession.id,
      payload: {
        cartId: activeSession.cartId,
        successPath: '/dashboard/shop-orders',
        cancelPath: toLocalizedPath('/checkout'),
        paymentProvider: state.selectedPaymentProvider,
      },
    })

    if (confirm.action === 'redirect' && confirm.checkoutUrl) {
      dispatch({ type: 'REDIRECT_STARTED' })
      replaceCheckoutStepInUrl('redirecting_to_payment')
      window.location.assign(confirm.checkoutUrl)
      return
    }

    clearActiveCheckoutSessionId()
    await onCheckoutCompleted(activeSession.cartId)
    dispatch({ type: 'CONFIRM_COMPLETED', result: confirm })
    dispatch({ type: 'SET_STEP', step: 'completed' })
    replaceCheckoutStepInUrl('completed')
  } catch (error) {
    const mapped = resolveSubmitError({ error, fallbackCode: 'confirm_payment_failed' })
    dispatch({ type: 'SET_ERROR', code: mapped.code, detail: mapped.detail })
    dispatch({ type: 'SUBMIT_COMPLETED' })
  }
}
